import React, { useState, useEffect } from 'react';
import { usuarioService } from '../../services/entitiesServices';
import type { Usuario } from '../../types/entities';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface UsuarioTableProps {
  onEdit?: (usuario: Usuario) => void;
  onRefresh?: () => void;
}

export const UsuarioTable: React.FC<UsuarioTableProps> = ({ onEdit, onRefresh }) => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const notifier = useNotifier();

  const loadUsuarios = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await usuarioService.list();
      const data = Array.isArray(response) ? response : (response as any).usuarios || response.data || [];
      setUsuarios(data);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
      setError(err instanceof Error ? err.message : 'Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsuarios();
  }, []);

  useEffect(() => {
    if (onRefresh) {
      loadUsuarios();
    }
  }, [onRefresh]);

  const handleDelete = (id: string) => {
    setDeleteTargetId(id);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetId) return;
    setConfirmOpen(false);
    try {
      await usuarioService.delete(deleteTargetId);
      notifier.show({ message: 'Usuário excluído com sucesso', severity: 'success' });
      await loadUsuarios();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir usuário');
    } finally {
      setDeleteTargetId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-xs" style={{ color: '#9E9E9E' }}>Carregando usuários...</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {error && (
        <div className="p-3 rounded-md bg-red-50">
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border rounded-lg" style={{ borderColor: '#C3B4DE' }}>
          <thead>
            <tr style={{ backgroundColor: '#F4EFFA' }}>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>Nome</th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>Email</th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>Tipo</th>
              <th className="border px-4 py-3 text-left text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>Empresa/Grupo</th>
              <th className="border px-4 py-3 text-center text-xs font-medium" style={{ color: '#5d54a4', borderColor: '#C3B4DE' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {usuarios.length === 0 ? (
              <tr>
                <td colSpan={5} className="border px-4 py-8 text-center text-xs" style={{ color: '#9E9E9E', borderColor: '#C3B4DE' }}>
                  Nenhum usuário cadastrado
                </td>
              </tr>
            ) : (
              usuarios.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>{u.nome}</td>
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>{u.email}</td>
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>{u.ehGrupo ? 'Grupo' : 'Empresa'}</td>
                  <td className="border px-4 py-3 text-xs" style={{ color: '#121212', borderColor: '#C3B4DE' }}>{u.empresaId ?? u.grupoId ?? '-'}</td>
                  <td className="border px-4 py-3 text-center" style={{ borderColor: '#C3B4DE' }}>
                    <div className="flex justify-center space-x-2">
                      {onEdit && (
                        <button onClick={() => onEdit(u)} className="px-3 py-1 text-xs font-medium text-white rounded hover:opacity-80 transition-opacity" style={{ backgroundColor: '#7A61B2' }}>Editar</button>
                      )}
                      <button onClick={() => handleDelete(u.id)} className="px-3 py-1 text-xs font-medium text-white rounded hover:opacity-80 transition-opacity" style={{ backgroundColor: '#DC2626' }}>Excluir</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {usuarios.length > 0 && (
        <div className="text-xs" style={{ color: '#9E9E9E' }}>
          Total: {usuarios.length} usuário{usuarios.length !== 1 ? 's' : ''}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Excluir Usuário"
        message="Tem certeza que deseja excluir este usuário?"
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteTargetId(null); }}
      />
    </div>
  );
};
